import { useState, useCallback } from 'react';
import { usePost } from "../API";
import { useAsyncInterval } from '../Util';

/**
 * Provides a way to watch the status of an async job.
 *
 * Example usage:
 * ```
  const queueJob = usePost('/some_queue_endpoint');
 * const { phase, setTaskId } = useAsyncJobStatus(3_000);
 *
 * useEffect(() => {
 *  const taskId = await queueJob({});
 *  setTaskId(taskId);
 * }, []);
 *
 * useEffect(() => {
 *  console.log('job phase:', phase);
 * }, [phase]);
 * ```
 *
 * Phase gets updated periodically, based on the interval you choose.
 *
 * @param intervalInMillis the interval, in milliseconds, to run the status check
 *
 */
export function useAsyncJobStatus(intervalInMillis: number = 5_000) {

  const checkStatus = usePost('/program/admin/async_job_status');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [phase, setPhase] = useState<'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED' | 'NOT_SUBMITTED'>('NOT_SUBMITTED');

  const doCheck = useCallback(async (taskId: string) => {

    const { status } = await checkStatus({ taskId });

    switch (status) {
      case 'FINISHED':
      case 'FAILED':
        setShouldPoll(false);
        break;
      default:
        break;
    }

    if (status !== phase) {
      setPhase(status);
    }

  }, [taskId, shouldPoll, checkStatus, phase]);

  useAsyncInterval(async () => {
    if (!taskId || !shouldPoll) return;
    await doCheck(taskId);
  }, intervalInMillis);

  return {
    phase,
    taskId,
    setTaskId: useCallback((taskId: string) => {
      setShouldPoll(false);
      setTaskId(taskId);
      doCheck(taskId).then(() =>
        setShouldPoll(true)
      );
    }, [doCheck])
  };
}
